import React, { useState, useEffect, useRef, useCallback } from 'react';

const PlacesAutocomplete = ({ value, onChange, placeholder, className }) => {
    const [input, setInput]           = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen]             = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef  = useRef(null);
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

    useEffect(() => {
        setInput(value || '');
    }, [value]);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const fetchSuggestions = useCallback(async (text) => {
        if (!text || text.length < 2 || !apiKey) { setSuggestions([]); return; }
        try {
            const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': apiKey },
                body: JSON.stringify({ input: text }),
            });
            const data = await res.json();
            setSuggestions(data.suggestions || []);
            setOpen(true);
        } catch {
            setSuggestions([]);
        }
    }, [apiKey]);

    const handleChange = (e) => {
        const val = e.target.value;
        setInput(val);
        onChange(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
    };

    const handleSelect = (suggestion) => {
        const text = suggestion.placePrediction?.text?.text || '';
        setInput(text);
        onChange(text);
        setSuggestions([]);
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
            <input
                type="text"
                value={input}
                onChange={handleChange}
                placeholder={placeholder || 'Address or location name'}
                className={className}
                autoComplete="off"
            />
            {open && suggestions.length > 0 && (
                <ul style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    listStyle: 'none',
                    margin: '4px 0 0',
                    padding: 0,
                    maxHeight: '220px',
                    overflowY: 'auto',
                }}>
                    {suggestions.map((s, i) => {
                        const pred      = s.placePrediction;
                        const main      = pred?.structuredFormat?.mainText?.text || pred?.text?.text || '';
                        const secondary = pred?.structuredFormat?.secondaryText?.text || '';
                        return (
                            <li
                                key={i}
                                onMouseDown={() => handleSelect(s)}
                                onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}
                                style={{
                                    padding: '0.5rem 0.75rem',
                                    cursor: 'pointer',
                                    borderBottom: i < suggestions.length - 1 ? '1px solid #f5f5f5' : 'none',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4,
                                }}
                            >
                                <span style={{ fontWeight: 600 }}>{main}</span>
                                {secondary && (
                                    <span style={{ color: '#888', marginLeft: '0.35rem' }}>{secondary}</span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default PlacesAutocomplete;
